/**
 * Capability gating off the MCP's tools/list. Write controls render only when the
 * operator's role actually exposes the action (e.g. `can('setSkuPrice')`) — with no
 * hardcoded role list in the UI. The BFF + MCP still enforce the gate server-side;
 * this just hides controls the operator can't use.
 *
 * `can()` takes the same name a page passes to mcpCall/mcpWrite — the old camelCase
 * handler name — and answers from the ACTIONS the domain tools enumerate, not from the
 * tool names. Until 2026-09-03 it compared against tool names, and since the 2026-08-27
 * consolidation those are `cabana_<domain>`, so every `can('setSkuPrice')` was false and
 * every write control on the console was hidden. See lib/mcp.ts.
 */
import { useQuery } from '@tanstack/react-query';
import { mcpListTools, indexActions, actionName } from './mcp';

export function useMcpTools() {
  const q = useQuery({
    queryKey: ['mcp', 'tools'],
    queryFn: mcpListTools,
    staleTime: 5 * 60_000,
  });
  const tools = q.data ?? [];
  const names = new Set(tools.map((t) => t.name));
  const actions = indexActions(tools);
  return {
    names,
    /** True when some tool the operator may call offers this action. */
    can: (name: string) => actions.has(name.includes('_') ? name : actionName(name)),
    isLoading: q.isPending,
  };
}
